import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, PhoneOff, Loader2, Save, Info } from "lucide-react";

/* Click to call.

   Telnyx rings whichever phone the technician has saved, then dials the far end showing
   the PawLinx number, then joins the two. There is no softphone and nothing to install,
   so it works from a mobile, a landline, or a desk phone without any setup on the device.

   Everything this component does is visible to the person using it. It never dials
   without a press, it says out loud which phone is about to ring, and it shows the live
   state of the call rather than leaving them guessing. */

const STATE_LABEL = {
  dialing_agent: "Your phone is ringing",
  agent_answered: "Connecting you",
  dialing_target: "Ringing the other side",
  bridged: "Connected",
  completed: "Call finished",
  not_connected: "Nobody picked up",
  failed: "The call could not be placed",
};

const LIVE = ["dialing_agent", "agent_answered", "dialing_target", "bridged"];

function Pill({ children, tone = "info" }) {
  const tones = {
    info: "bg-paw-primary/10 text-paw-primary border-paw-primary/25 dark:bg-paw-primary/15 dark:text-blue-300 dark:border-blue-400/25",
    good: "bg-emerald-500/15 text-emerald-700 border-emerald-500/25 dark:text-emerald-300 dark:border-emerald-400/25",
    warn: "bg-amber-500/15 text-amber-700 border-amber-500/25 dark:text-amber-300 dark:border-amber-400/25",
    bad: "bg-red-500/15 text-red-700 border-red-500/25 dark:text-red-300 dark:border-red-400/25",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border font-manrope text-[11px] font-bold ${tones[tone]}`}>
      {children}
    </span>
  );
}

export default function CallButton({
  to,
  who = "",
  purpose = "clinic",
  requestId = "",
  estimateId = "",
  customerId = "",
  label = "",
  onFinished,
}) {
  const [cfg, setCfg] = useState(null);
  const [askNumber, setAskNumber] = useState(false);
  const [myNumber, setMyNumber] = useState("");
  const [savingNumber, setSavingNumber] = useState(false);
  const [call, setCall] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  const stop = useCallback(() => {
    if (poll.current) { clearInterval(poll.current); poll.current = null; }
  }, []);

  useEffect(() => stop, [stop]);

  const loadConfig = useCallback(async () => {
    const { data } = await api.get("/admin/call/config");
    setCfg(data);
    setMyNumber(data.my_number || "");
    return data;
  }, []);

  const watch = useCallback((callId) => {
    stop();
    poll.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/calls/${callId}`);
        setCall(data);
        if (!LIVE.includes(data.status)) {
          stop();
          if (onFinished) onFinished(data);
        }
      } catch {
        stop();
      }
    }, 2000);
  }, [stop, onFinished]);

  const saveNumber = async () => {
    setSavingNumber(true);
    try {
      const { data } = await api.post("/admin/call/my-number", { phone: myNumber });
      toast.success(`PawLinx will ring ${data.my_number_pretty}`);
      setAskNumber(false);
      await loadConfig();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingNumber(false); }
  };

  const start = async () => {
    if (!to) { toast.error("There is no phone number on this record."); return; }
    setBusy(true);
    try {
      const c = cfg || (await loadConfig());
      if (!c.my_number) { setAskNumber(true); setBusy(false); return; }
      if (!c.ready) {
        toast.error(c.missing.join(". "));
        setBusy(false);
        return;
      }
      const { data } = await api.post("/admin/call/dial", {
        to, who, purpose,
        request_id: requestId || null,
        estimate_id: estimateId || null,
        customer_id: customerId || null,
      });
      setCall({ call_id: data.call_id, status: "dialing_agent", target_pretty: data.calling });
      toast.success(`Answer your phone on ${data.ringing}`);
      watch(data.call_id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const hangup = async () => {
    if (!call) return;
    try {
      await api.post(`/admin/calls/${call.call_id}/hangup`, {});
      stop();
      setCall({ ...call, status: "completed" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const live = call && LIVE.includes(call.status);
  const tone = call
    ? (call.status === "bridged" ? "good"
      : call.status === "failed" ? "bad"
      : call.status === "not_connected" ? "warn" : "info")
    : "info";

  return (
    <div className="space-y-2">
      {!live && (
        <button
          onClick={start}
          disabled={busy || !to}
          className="h-9 px-3 rounded-lg bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-40 text-white font-manrope text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
          data-testid="call-start"
          title={to ? `Call ${to}` : "No phone number on this record"}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
          {label || (purpose === "customer" ? "Call the customer" : "Call the clinic")}
        </button>
      )}

      {askNumber && (
        <div className="rounded-lg border border-paw-border dark:border-white/10 bg-paw-cream/60 dark:bg-white/5 p-3" data-testid="call-setup">
          <p className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 leading-relaxed mb-2">
            Which phone should PawLinx ring? This one call rings you first, then dials the other
            side and joins you. Your own number is never shown to them.
          </p>
          <div className="flex items-center gap-2">
            <input
              value={myNumber}
              onChange={(e) => setMyNumber(e.target.value)}
              placeholder="416 555 0134"
              className="flex-1 h-9 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 px-3 font-manrope text-sm text-paw-text dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-paw-primary/40"
              data-testid="call-my-number"
            />
            <button
              onClick={saveNumber}
              disabled={savingNumber || !myNumber.trim()}
              className="h-9 px-3 rounded-lg bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-40 text-white font-manrope text-xs font-semibold inline-flex items-center gap-1.5 transition-colors"
              data-testid="call-save-number"
            >
              {savingNumber ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
          </div>
        </div>
      )}

      {cfg && !cfg.ready && cfg.my_number && cfg.missing.length > 0 && !live && (
        <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 dark:bg-amber-400/10 p-3 flex gap-2" data-testid="call-not-ready">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="font-manrope text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
            Calling is not switched on yet. {cfg.missing.join(". ")}.
          </div>
        </div>
      )}

      {call && (
        <div className="rounded-lg border border-paw-border dark:border-white/10 bg-white dark:bg-[#12141c] p-3" data-testid="call-status">
          <div className="flex items-center gap-2 flex-wrap">
            <Pill tone={tone}>
              {live ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              {STATE_LABEL[call.status] || call.status}
            </Pill>
            {call.target_pretty ? (
              <span className="font-manrope text-[11px] text-paw-text-muted dark:text-white/40">
                {who ? `${who}, ` : ""}{call.target_pretty}
              </span>
            ) : null}
            {live && (
              <button
                onClick={hangup}
                className="ml-auto h-8 px-3 rounded-lg border border-red-500/30 text-red-600 dark:text-red-300 font-manrope text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-red-500/10"
                data-testid="call-hangup"
              >
                <PhoneOff className="w-3.5 h-3.5" /> End
              </button>
            )}
          </div>
          {call.status === "failed" && call.error ? (
            <p className="mt-2 font-manrope text-xs text-red-600 dark:text-red-300 leading-relaxed">{call.error}</p>
          ) : null}
          {call.status === "completed" && call.talk_seconds ? (
            <p className="mt-2 font-manrope text-xs text-paw-text-secondary dark:text-white/50">
              You spoke for {Math.floor(call.talk_seconds / 60)}m {call.talk_seconds % 60}s.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
